/**
 * usePullToRefresh — lightweight native-feeling pull-to-refresh for touch devices.
 *
 * Usage:
 *   const { pullProps, pullDistance, isRefreshing, threshold } = usePullToRefresh(async () => {
 *     await fetchStuff();
 *   });
 *
 *   return (
 *     <div {...pullProps} className="overflow-y-auto">
 *       <PullIndicator distance={pullDistance} threshold={threshold} refreshing={isRefreshing} />
 *       ...content
 *     </div>
 *   );
 */
import { useCallback, useRef, useState } from 'react';

const DEFAULT_THRESHOLD = 70;   // px pull required to trigger
const MAX_PULL = 120;           // clamp pulled-down distance
const DRAG_RESISTANCE = 0.45;   // rubber-band factor

export function usePullToRefresh(onRefresh, { threshold = DEFAULT_THRESHOLD } = {}) {
  const startY = useRef(null);
  const pulling = useRef(false);
  const [pullDistance, setPullDistance] = useState(0);
  const [isRefreshing, setIsRefreshing] = useState(false);

  const handleTouchStart = useCallback((e) => {
    const target = e.currentTarget;
    // Only engage if the scroll container is at the top.
    if (!target || target.scrollTop > 0) {
      startY.current = null;
      pulling.current = false;
      return;
    }
    if (e.touches && e.touches.length === 1) {
      startY.current = e.touches[0].clientY;
      pulling.current = true;
    }
  }, []);

  const handleTouchMove = useCallback((e) => {
    if (!pulling.current || startY.current == null) return;
    const target = e.currentTarget;
    if (target.scrollTop > 0) {
      pulling.current = false;
      setPullDistance(0);
      return;
    }
    const deltaY = e.touches[0].clientY - startY.current;
    if (deltaY <= 0) {
      setPullDistance(0);
      return;
    }
    // Rubber-band: progressively stiffer as you pull further.
    const eased = Math.min(MAX_PULL, deltaY * DRAG_RESISTANCE);
    setPullDistance(eased);
  }, []);

  const handleTouchEnd = useCallback(async () => {
    if (!pulling.current) {
      setPullDistance(0);
      return;
    }
    pulling.current = false;
    startY.current = null;
    if (pullDistance >= threshold && !isRefreshing) {
      setIsRefreshing(true);
      try {
        await onRefresh?.();
      } finally {
        setIsRefreshing(false);
        setPullDistance(0);
      }
    } else {
      setPullDistance(0);
    }
  }, [onRefresh, pullDistance, threshold, isRefreshing]);

  const pullProps = {
    onTouchStart: handleTouchStart,
    onTouchMove: handleTouchMove,
    onTouchEnd: handleTouchEnd,
    onTouchCancel: handleTouchEnd,
  };

  return { pullProps, pullDistance, isRefreshing, threshold };
}
